import { z } from 'zod';
import { MoneyNum } from './numbers';
import {
  FUNDING_SOURCES,
  type FundingSource,
  PAYMENT_PRIORITIES,
  PAYMENT_REQUEST_STATUSES,
  type PaymentPriority,
  type PaymentRequestStatus,
} from './procurement';

/* ───────────────────────── Платёжный календарь (ФН-02) ───────────────────────── */

const Money = z.number().finite();
const DateStr = z.iso.date();

/**
 * Строка платёжного календаря: плановый платёж из графика договора, ещё не закрытый заявками на
 * оплату, или сама заявка на оплату, которая его закрывает.
 */
export const CalendarRow = z.object({
  kind: z.enum(['planned', 'request', 'fact']),
  /** planned — строка графика договора, request — заявка на оплату, fact — оплата, внесённая без заявки */
  id: z.uuid(),
  date: DateStr.nullable(),
  /** Дата по договору, если её нет — по основанию платежа */
  dateNote: z.string().nullable(),
  projectId: z.uuid(),
  projectCode: z.string(),
  legalEntityId: z.uuid(),
  contractId: z.uuid().nullable(),
  contractNumber: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  description: z.string(),
  amount: Money,
  number: z.string().nullable(),
  status: z
    .enum(Object.keys(PAYMENT_REQUEST_STATUSES) as [PaymentRequestStatus, ...PaymentRequestStatus[]])
    .nullable(),
  priority: z.enum(Object.keys(PAYMENT_PRIORITIES) as [PaymentPriority, ...PaymentPriority[]]),
  fundingSource: z.enum(Object.keys(FUNDING_SOURCES) as [FundingSource, ...FundingSource[]]).nullable(),
  costArticle: z.string().nullable(),
  overdue: z.boolean(),
  /** Заявка на оплату ссылается на плановый платёж договора */
  scheduleRowId: z.uuid().nullable(),
  moves: z.number().int(),
});
export type CalendarRow = z.infer<typeof CalendarRow>;

export const CalendarPeriod = z.object({
  /** День (YYYY-MM-DD) или понедельник недели */
  start: DateStr,
  planned: Money,
  requested: Money,
  paid: Money,
  limit: Money.nullable(),
  over: z.boolean(),
});
export type CalendarPeriod = z.infer<typeof CalendarPeriod>;

export const PaymentCalendar = z.object({
  from: DateStr,
  to: DateStr,
  rows: z.array(CalendarRow),
  /** Плановые платежи без даты: «по поставке», «накладная + N дней» до поставки */
  undated: z.array(CalendarRow),
  days: z.array(CalendarPeriod),
  weeks: z.array(CalendarPeriod),
  limits: z.object({ daily: Money.nullable(), weekly: Money.nullable() }),
  /**
   * Лимиты — на платежи всей компании: они видны и сравниваются в календаре всей компании (без фильтра
   * проекта и у того, кто видит платежи всех проектов); в остальных случаях лимитов в ответе нет
   */
  limitsShown: z.boolean(),
  totals: z.object({ planned: Money, requested: Money, paid: Money, overdue: Money }),
  can: z.object({ limits: z.boolean() }),
});
export type PaymentCalendar = z.infer<typeof PaymentCalendar>;

export const PaymentLimitsInput = z.object({
  daily: MoneyNum.positive().nullable(),
  weekly: MoneyNum.positive().nullable(),
});
export type PaymentLimitsInput = z.infer<typeof PaymentLimitsInput>;

/** Настройка комплектов: документ считается полученным, только когда к нему приложен файл (ФН-19) */
export const PaymentDocSettingsInput = z.object({ requireFile: z.boolean() });
export type PaymentDocSettingsInput = z.infer<typeof PaymentDocSettingsInput>;

/* ───────────────────────── Настройки компании (АД-08) ───────────────────────── */

export const CompanySettings = z.object({
  /** Окно проверки дробления закупок, дней (ЗК-33) */
  splitWindowDays: z.number().int(),
  /** Порог трёх КП, ₸ (ЗК-05): выше — три КП или обоснование; по умолчанию 300 000 */
  threeOffersLimit: z.number(),
  can: z.object({ edit: z.boolean() }),
});
export type CompanySettings = z.infer<typeof CompanySettings>;

export const CompanySettingsInput = z.object({
  splitWindowDays: z.number().int().min(7, 'Не меньше 7 дней').max(365, 'Не больше 365 дней'),
  /** Не передан — порог не меняется (прежние страницы шлют только окно) */
  threeOffersLimit: z.number().int().min(0, 'Не меньше 0').max(1e12, 'Слишком большая сумма').optional(),
});
export type CompanySettingsInput = z.infer<typeof CompanySettingsInput>;

/* ───────────────── Авансы и гарантийные удержания (ФН-06) ───────────────── */

export const ADVANCE_SIDES = {
  issued: 'Выдан подрядчику',
  received: 'Получен от заказчика',
} as const;
export type AdvanceSide = keyof typeof ADVANCE_SIDES;

export const RETENTION_STATUSES = {
  held: 'Удерживается',
  returned: 'Возвращено',
  written_off: 'Списано',
} as const;
export type RetentionStatus = keyof typeof RETENTION_STATUSES;

const AdvanceOffsetView = z.object({
  id: z.uuid(),
  amount: Money,
  offsetOn: DateStr,
  actNumber: z.string().nullable(),
  note: z.string().nullable(),
});
export type AdvanceOffsetView = z.infer<typeof AdvanceOffsetView>;

export const AdvanceRow = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  counterpartyName: z.string(),
  side: z.enum(Object.keys(ADVANCE_SIDES) as [AdvanceSide, ...AdvanceSide[]]),
  amount: Money,
  offset: Money,
  remaining: Money,
  paidOn: DateStr.nullable(),
  note: z.string().nullable(),
  offsets: z.array(AdvanceOffsetView),
});
export type AdvanceRow = z.infer<typeof AdvanceRow>;

const AdvanceSideTotals = z.object({
  amount: Money,
  offset: Money,
  remaining: Money,
});

export const AdvancesResponse = z.object({
  rows: z.array(AdvanceRow),
  totals: z.object({
    issued: AdvanceSideTotals,
    received: AdvanceSideTotals,
  }),
  can: z.object({ edit: z.boolean() }),
});
export type AdvancesResponse = z.infer<typeof AdvancesResponse>;

export const AdvanceInput = z.object({
  contractId: z.uuid(),
  side: z.enum(Object.keys(ADVANCE_SIDES) as [AdvanceSide, ...AdvanceSide[]]),
  amount: MoneyNum.positive('Сумма аванса должна быть больше нуля'),
  paidOn: DateStr.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  scheduleRowId: z.uuid().nullable().optional(),
});
export type AdvanceInput = z.infer<typeof AdvanceInput>;

export const AdvanceOffsetInput = z.object({
  amount: MoneyNum.positive('Сумма зачёта должна быть больше нуля'),
  offsetOn: DateStr,
  actNumber: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type AdvanceOffsetInput = z.infer<typeof AdvanceOffsetInput>;

export const RetentionRow = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  counterpartyName: z.string(),
  amount: Money,
  withheldOn: DateStr.nullable(),
  returnPlanned: DateStr,
  returnedOn: DateStr.nullable(),
  status: z.enum(Object.keys(RETENTION_STATUSES) as [RetentionStatus, ...RetentionStatus[]]),
  /** Срок возврата наступил, а удержание ещё не возвращено */
  overdue: z.boolean(),
  actNumber: z.string().nullable(),
  note: z.string().nullable(),
});
export type RetentionRow = z.infer<typeof RetentionRow>;

export const RetentionsResponse = z.object({
  rows: z.array(RetentionRow),
  totals: z.object({
    held: Money,
    overdue: Money,
    returned: Money,
  }),
  can: z.object({ edit: z.boolean() }),
});
export type RetentionsResponse = z.infer<typeof RetentionsResponse>;

export const RetentionInput = z.object({
  contractId: z.uuid(),
  amount: MoneyNum.positive('Сумма удержания должна быть больше нуля'),
  withheldOn: DateStr.nullable().optional(),
  returnPlanned: DateStr,
  actNumber: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  scheduleRowId: z.uuid().nullable().optional(),
});
export type RetentionInput = z.infer<typeof RetentionInput>;

export const RetentionPatch = z
  .object({
    returnPlanned: DateStr.optional(),
    returnedOn: DateStr.nullable().optional(),
    status: z.enum(Object.keys(RETENTION_STATUSES) as [RetentionStatus, ...RetentionStatus[]]).optional(),
    actNumber: z.string().trim().max(120).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Нечего менять' });
export type RetentionPatch = z.infer<typeof RetentionPatch>;

/* ───────────────────── Реестр платежей на утверждение (ФН-03, ФН-15, ФН-16) ───────────────────── */

export const PAYMENT_REGISTRY_STATUSES = {
  draft: 'Собирается',
  submitted: 'На утверждении',
  approved: 'Утверждён',
  cancelled: 'Отменён',
} as const;
export type PaymentRegistryStatus = keyof typeof PAYMENT_REGISTRY_STATUSES;

/** РП-2026-0001: реестр платежей (не путать с реестром закупок РЗ) */
export function paymentRegistryNumber(year: number, n: number): string {
  return `РП-${year}-${String(n).padStart(4, '0')}`;
}

/** Строка реестра или кандидат в реестр: заявка на оплату и что мешает её оплатить по ФН-15 */
export const PaymentRegistryLine = z.object({
  /** Строка реестра; у кандидата — null */
  lineId: z.uuid().nullable(),
  paymentRequestId: z.uuid(),
  /** Заявка на закуп, к которой относится заявка на оплату: ссылка из строки */
  purchaseRequestId: z.uuid().nullable(),
  number: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  contractId: z.uuid().nullable(),
  contractNumber: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  costArticle: z.string().nullable(),
  plannedDate: DateStr.nullable(),
  purpose: z.string().nullable(),
  amount: Money,
  requestStatus: z.enum(
    Object.keys(PAYMENT_REQUEST_STATUSES) as [PaymentRequestStatus, ...PaymentRequestStatus[]],
  ),
  /** in — в реестре, out — исключена; у кандидата — null */
  state: z.enum(['in', 'out']).nullable(),
  outReason: z.string().nullable(),
  /** Что мешает оплате сейчас: договор, статья ФП, ДДС, основание. Пусто — можно в реестр */
  issues: z.array(z.string()),
});
export type PaymentRegistryLine = z.infer<typeof PaymentRegistryLine>;

export const PaymentRegistrySummary = z.object({
  id: z.uuid(),
  number: z.string(),
  legalEntityId: z.uuid(),
  legalEntityName: z.string(),
  payDate: DateStr,
  status: z.enum(
    Object.keys(PAYMENT_REGISTRY_STATUSES) as [PaymentRegistryStatus, ...PaymentRegistryStatus[]],
  ),
  version: z.number().int(),
  count: z.number().int(),
  total: Money,
  /** Сколько заявок реестра уже оплачено */
  paidCount: z.number().int(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  submittedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
});
export type PaymentRegistrySummary = z.infer<typeof PaymentRegistrySummary>;

export const PaymentRegistryView = PaymentRegistrySummary.extend({
  payerAccount: z.string().nullable(),
  note: z.string().nullable(),
  submittedByName: z.string().nullable(),
  returnReason: z.string().nullable(),
  cancelReason: z.string().nullable(),
  exported1cAt: z.string().nullable(),
  exportedBankAt: z.string().nullable(),
  lines: z.array(PaymentRegistryLine),
  can: z.object({
    edit: z.boolean(),
    approve: z.boolean(),
    export: z.boolean(),
    /** Снять заявку из утверждённого реестра (банк отклонил платёж, заявку отменили) */
    drop: z.boolean(),
  }),
});
export type PaymentRegistryView = z.infer<typeof PaymentRegistryView>;

export const PaymentRegistriesResponse = z.object({
  registries: z.array(PaymentRegistrySummary),
  legalEntities: z.array(z.object({ id: z.uuid(), name: z.string() })),
  can: z.object({ create: z.boolean() }),
});
export type PaymentRegistriesResponse = z.infer<typeof PaymentRegistriesResponse>;

/** Отбор кандидатов: по проекту, договору, сроку и сумме (ФН-03) */
export const PaymentRegistryCandidatesQuery = z.object({
  projectId: z.uuid().optional(),
  contractId: z.uuid().optional(),
  dueTo: DateStr.optional(),
  minAmount: z.coerce.number().finite().min(0).optional(),
  maxAmount: z.coerce.number().finite().min(0).optional(),
});
export type PaymentRegistryCandidatesQuery = z.infer<typeof PaymentRegistryCandidatesQuery>;

const Account = z
  .string()
  .trim()
  .max(34)
  .regex(/^[A-Z0-9]*$/i, 'Счёт — латинские буквы и цифры (ИИК)');

export const PaymentRegistryInput = z.object({
  legalEntityId: z.uuid(),
  payDate: DateStr,
  payerAccount: Account.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type PaymentRegistryInput = z.infer<typeof PaymentRegistryInput>;

export const PaymentRegistryPatch = z.object({
  version: z.number().int(),
  payDate: DateStr.optional(),
  payerAccount: Account.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type PaymentRegistryPatch = z.infer<typeof PaymentRegistryPatch>;

export const PaymentRegistryLinesInput = z.object({
  version: z.number().int(),
  paymentRequestIds: z.array(z.uuid()).min(1).max(200),
});
export type PaymentRegistryLinesInput = z.infer<typeof PaymentRegistryLinesInput>;

export const PaymentRegistryAction = z.object({
  /** drop — снять заявки из утверждённого реестра (строки и причины — в exclude) */
  action: z.enum(['submit', 'return', 'approve', 'cancel', 'drop']),
  /** Версия реестра, которую видел человек: изменённый после этого реестр не утверждается */
  version: z.number().int(),
  reason: z.string().trim().max(2000).nullable().optional(),
  /** При утверждении: строки, которые утверждающий исключает, с причиной */
  exclude: z
    .array(z.object({ lineId: z.uuid(), reason: z.string().trim().min(3).max(500) }))
    .max(200)
    .optional(),
});
export type PaymentRegistryAction = z.infer<typeof PaymentRegistryAction>;
